import { useState } from "react";
import { Link } from "react-router-dom";
import type { ReviewEntry, ReviewQuestionPayload } from "../../api/admin";
import { apiErrorDetail } from "../../api/client";
import { agentDrawerUrl } from "../../lib/aiTeam";
import { draftKey, useDraft } from "../../lib/hooks/useDraft";
import { Markdown } from "../Markdown";

const DELIVERY_LABELS = {
  queued: "Queued for employee", working: "Employee is answering",
  waiting: "Waiting for input", answered: "Answered", failed: "Answer failed",
  canceled: "Question job canceled", unanswered: "No answer recorded",
};

// A debrief is the owner's notes from closing a briefing – their side, like a question.
const ENTRY_LABELS: Record<ReviewEntry["kind"], string> = {
  question: "Question", answer: "Answer", note: "Note", changelog: "Changelog", debrief: "Debrief",
};

function storedRequest(key: string): ReviewQuestionPayload | null {
  try {
    const value = JSON.parse(localStorage.getItem(key) ?? "null");
    return value && typeof value.request_id === "string" && typeof value.body_md === "string"
      && Number.isInteger(value.content_version) ? value : null;
  } catch { return null; }
}

export function ReviewConversation({ reviewId, agentId, agentLabel, version, entries, pending, onAsk }: {
  reviewId: string;
  agentId: string;
  agentLabel: string;
  version: number;
  entries: ReviewEntry[];
  pending: boolean;
  onAsk: (payload: ReviewQuestionPayload) => Promise<unknown>;
}) {
  const draft = useDraft(draftKey("review-question", reviewId));
  const requestKey = `dux-review-question-request:${reviewId}`;
  const [lastRequest, setLastRequest] = useState(() => storedRequest(requestKey));
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const ask = async () => {
    const body = draft.draft.trim();
    if (!body || pending) return;
    const payload = lastRequest?.body_md === body ? lastRequest : {
      request_id: crypto.randomUUID(), body_md: body, content_version: version,
    };
    // Persist before sending: a lost response or reload must reuse the same ID.
    setLastRequest(payload);
    try { localStorage.setItem(requestKey, JSON.stringify(payload)); } catch { /* storage unavailable */ }
    setError(null);
    setSent(false);
    try {
      await onAsk(payload);
      draft.clearDraft();
      setLastRequest(null);
      try { localStorage.removeItem(requestKey); } catch { /* storage unavailable */ }
      setSent(true);
    } catch (e) {
      setError(apiErrorDetail(e));
    }
  };

  return (
    <section className="mb-6 pb-5 border-b border-rule" aria-labelledby="review-conversation-heading">
      <h2 id="review-conversation-heading" className="font-heading font-semibold mb-2">Conversation &amp; updates</h2>
      <p className="text-xs text-ink-low mb-3">Questions, answers and updates from your employees.</p>
      {entries.length === 0 ? <p className="text-sm text-ink-low mb-4">No conversation yet.</p> : (
        <ol className="space-y-3 mb-4 lg:max-h-[55vh] overflow-y-auto break-words" aria-label="Review conversation">
          {entries.map((entry) => {
            const question = entry.reply_to_id ? entries.find((e) => e.id === entry.reply_to_id) : null;
            return (
              <li id={`review-entry-${entry.id}`} key={entry.id}
                className={`border border-rule rounded-lg p-3 ${entry.kind === "question" || entry.kind === "debrief" ? "ml-5 bg-aqua-tint" : "mr-2"}`}>
                <div className="flex flex-wrap gap-x-2 text-xs text-ink-low mb-2">
                  <strong className="text-ink">{entry.author_label}</strong>
                  <span>{ENTRY_LABELS[entry.kind]}</span>
                  <span>v{entry.content_version}</span>
                  <time dateTime={entry.created_at}>{new Date(entry.created_at.endsWith("Z") || /[+-]\d{2}:\d{2}$/.test(entry.created_at) ? entry.created_at : `${entry.created_at}Z`).toLocaleString()}</time>
                </div>
                {entry.recorded_question_md && (
                  <blockquote className="border-l-2 border-rule pl-3 mb-3">
                    <p className="text-xs text-ink-low mb-1">Question recorded by {entry.author_label}</p>
                    <Markdown text={entry.recorded_question_md} />
                  </blockquote>
                )}
                {question && (
                  <blockquote className="border-l-2 border-rule pl-3 mb-3">
                    <a className="text-xs link" href={`#review-entry-${question.id}`}>Reply to {question.author_label}</a>
                    <Markdown text={question.body_md} />
                  </blockquote>
                )}
                <Markdown text={entry.body_md} />
                {entry.delivery_state && (
                  <p className="text-xs text-ink-low mt-2">
                    {DELIVERY_LABELS[entry.delivery_state]}
                    {entry.job_id && entry.delivery_state !== "answered" && <> · <Link className="link" to={agentDrawerUrl(agentId, { jobId: entry.job_id })}>Job details</Link></>}
                  </p>
                )}
              </li>
            );
          })}
        </ol>
      )}
      <form onSubmit={(event) => { event.preventDefault(); void ask(); }} className="space-y-2">
        <label htmlFor="review-question" className="text-sm font-semibold">Ask {agentLabel} · v{version}</label>
        <textarea id="review-question" className="input w-full" rows={3} maxLength={20000}
          value={draft.draft} onChange={(e) => { draft.setDraft(e.target.value); setSent(false); }}
          disabled={pending} placeholder="What would you like to understand?" />
        <button type="submit" className="btn btn-sm btn-main" disabled={pending || !draft.draft.trim()}>
          {pending ? "Sending…" : "Ask employee"}
        </button>
        <p className="text-xs text-ink-low">Sends immediately. The employee replies here when available. Your review decision stays unchanged.</p>
        {error && <p role="alert" className="text-sm text-ink">{error} Your question is still in the box; you can retry.</p>}
        {sent && <p role="status" className="text-xs text-ink-low">Question saved.</p>}
      </form>
    </section>
  );
}
